import React, {ReactNode} from 'react'
import {useDispatch} from 'react-redux'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {WidgetLabelTypes} from 'utils/label'

import {Card} from '@daedalus/atlas/Card'
import {Grid} from '@daedalus/atlas/helpers/Grid'
import {Link} from '@daedalus/atlas/Link'
import {Text} from '@daedalus/atlas/Text'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {brands} from '@daedalus/core/src/brand/config'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {BrandLogo} from '@daedalus/shared/src/whiteLabel/BrandLogo'

interface WidgetContainerProps {
  /** Widget height in pixels */
  height: number
  /** Widget content */
  children: ReactNode
  /** Unique partner id */
  partnerKey: string
  /** Wrapped widget type */
  widgetType: WidgetLabelTypes
  /** Tracking label */
  label?: string
  /** Whether to hide container footer content */
  hideFooter?: boolean
  /** Widget border type */
  borderType: 'solid' | 'transparent' | 'none'
}

const ContainerCard = styled(Card)<{height: WidgetContainerProps['height']}>(
  ({height}) => css`
    display: flex;
    flex-direction: column;
    height: ${height}px;
  `
)

const WidgetFooter = styled(Grid)(
  ({theme}) => css`
    width: 100%;
    padding: ${theme.layout.spacing.s600}px 0 ${theme.layout.spacing.s500}px;
  `
)

const Brand = styled(BrandLogo)`
  width: 63px;
  margin: 0;
`

const BrandPoweredWrapper = styled('div')(
  ({theme}) => css`
    gap: ${theme.layout.spacing.s100}px;
    display: flex;
    flex-direction: row;
  `
)

const LinkComponent = styled(Link)(
  ({theme}) => css`
    text-decoration-color: ${theme.colors.content.neutral.c600} !important;
  `
)

export const WidgetContainer = ({
  height,
  children,
  partnerKey,
  widgetType,
  label,
  hideFooter,
  borderType
}: WidgetContainerProps) => {
  const dispatch = useDispatch()

  const trackLogoClick = () => {
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.VioLogo,
        action: Action.Clicked,
        payload: {
          partnerKey,
          widgetType,
          label
        }
      })
    )
  }

  const borderColor =
    borderType === 'transparent' || borderType === 'none'
      ? undefined
      : 'border.neutral.c100'

  const borderRadius =
    borderType === 'solid' || borderType === 'transparent' ? 'lg' : undefined

  return (
    <ContainerCard
      borderRadius={borderRadius}
      borderColorPath={borderColor}
      height={height}
    >
      {children}
      {!hideFooter && (
        <WidgetFooter
          container
          spacing="s000"
          alignItems="center"
          justify="center"
          direction="row"
          gap="s300"
        >
          <BrandPoweredWrapper>
            <Text variant="bodyXS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper
                id="partners.widget.poweredBy"
                defaultMessage="Powered by"
              />
            </Text>
            <Link
              href="https://www.vio.com"
              target="_blank"
              onClick={trackLogoClick}
            >
              <Brand brand={brands.vio} />
            </Link>
          </BrandPoweredWrapper>

          <LinkComponent
            href="https://forms.gle/8aJJtnHA3peAkcLU6"
            target="_blank"
          >
            <Text variant="bodyXS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper
                id="partners.widget.becomePartner"
                defaultMessage="Become a Partner."
              />
            </Text>
          </LinkComponent>
        </WidgetFooter>
      )}
    </ContainerCard>
  )
}
